import React from "react"
import {Box, Drawer, Link, Typography} from "@mui/material"

import type {CaseManual} from "@indebted/api"
import {colors} from "@indebted/theme/colors"

type CaseManualsDrawerProps = {
	open: boolean
	caseType: string
	manuals: CaseManual[]
	onClose: () => void
}

function CaseManualsDrawer({open, caseType, manuals, onClose}: CaseManualsDrawerProps): JSX.Element {
	return (
		<Drawer anchor="right" open={open} onClose={onClose}>
			<Box width={480} maxWidth="100vw" p={4} display="flex" flexDirection="column" gap={4}>
				<Box>
					<Typography variant="h6">Client &amp; case manuals</Typography>
					<Typography variant="body2" color="textSecondary">
						{caseType}
					</Typography>
				</Box>

				{manuals.length === 0 && (
					<Typography variant="body2" color="textSecondary">
						No manuals are written for this client and case type yet.
					</Typography>
				)}

				{manuals.map((manual) => (
					<Box key={manual.Name} display="flex" flexDirection="column" gap={1}>
						<Typography variant="subtitle2">{manual.Name}</Typography>
						<RuleText text={manual.Text} />
					</Box>
				))}
			</Box>
		</Drawer>
	)
}

// Rule text is markdown, but only ever paragraphs and links: a blank line
// starts a paragraph, and a single newline is wrapping in the source file
// rather than a break the agent should see.
function RuleText({text}: {text: string}): JSX.Element {
	return (
		<>
			{text
				.split(/\n[ \t]*\n/)
				.map((paragraph) => paragraph.replace(/\s*\n\s*/g, " ").trim())
				.filter((paragraph) => paragraph !== "")
				.map((paragraph, i) => (
					<Typography key={i} variant="body2" color="textPrimary">
						{linkify(paragraph)}
					</Typography>
				))}
		</>
	)
}

const markdownLink = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g

// Returns React nodes rather than markup, so rule text can never introduce
// anything but the links it declares.
function linkify(paragraph: string): React.ReactNode[] {
	const nodes: React.ReactNode[] = []
	let cursor = 0

	for (const match of paragraph.matchAll(markdownLink)) {
		const [whole, label, url] = match
		const start = match.index ?? 0

		if (start > cursor) nodes.push(paragraph.slice(cursor, start))
		nodes.push(
			<Link
				key={`${url}-${start}`}
				href={url}
				target="_blank"
				rel="noopener noreferrer"
				sx={{color: colors.warmBlue["900"]}}
			>
				{label}
			</Link>,
		)
		cursor = start + whole.length
	}

	if (cursor < paragraph.length) nodes.push(paragraph.slice(cursor))

	return nodes
}

export {CaseManualsDrawer}
